/**
 * Wizard Step 3 — Checkliste (COL-529).
 *
 * One collapsible card per apartment, each embedding the controlled
 * ChecklistV3. Edits are wired back through `setApartments` (the only
 * apartment mutator) by rebuilding the array with the changed apartment's
 * `checklist` replaced. Nothing persists here — the submit hooks write the
 * checklist on Review (a later chunk); this step never blocks advancing.
 */
import { ClipboardCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useWizard } from "./wizardContext";
import { ChecklistV3 } from "./ChecklistV3";
import {
  bedroomsOf,
  computeChecklistProgress,
  type ChecklistState,
} from "./checklist";

export function Step3Checklist() {
  const { t } = useLanguage();
  const { isOwnerPortalOnly } = useAuth();
  const { state, setApartments } = useWizard();
  const apartments = state.apartments;

  const setChecklist = (index: number, next: ChecklistState) => {
    setApartments(
      apartments.map((apt, i) =>
        i === index ? { ...apt, checklist: next } : apt
      )
    );
  };

  return (
    <Card className="p-6 space-y-6">
      <div className="flex items-start gap-3">
        <ClipboardCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">
            {t("admin.checklist.heading", "Checklist")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {isOwnerPortalOnly && state.checklistGrandfathered
              ? t(
                  "admin.checklist.introOwnerGrandfathered",
                  "You can resubmit without completing every proof. Finish the remaining items whenever you're ready — only an explicit smoke-detector “no” blocks you."
                )
              : isOwnerPortalOnly
                ? t(
                    "admin.checklist.introOwner",
                    "Every apartment needs all 15 proofs before you can submit. Complete each item for every apartment to continue."
                  )
                : t(
                    "admin.checklist.intro",
                    "Every apartment needs 15 proofs before it can go live. You can add a property now and complete these later — nothing here blocks creating the property."
                  )}
          </p>
        </div>
      </div>

      {apartments.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t(
            "admin.checklist.noApartments",
            "Add an apartment first to track its checklist."
          )}
        </p>
      ) : (
        <div className="space-y-3">
          {apartments.map((apt, i) => {
            const bedrooms = bedroomsOf(apt.numRooms);
            const progress = computeChecklistProgress(apt.checklist, {
              bedrooms,
              keynestAvailable: state.property.keynestAvailable,
            });
            const label =
              apt.label?.trim() ||
              `${t("admin.checklist.apartmentLabel", "Apartment")} ${i + 1}`;
            return (
              <Collapsible
                key={apt.localId}
                defaultOpen={apartments.length === 1}
                className="rounded-lg border"
              >
                <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 p-4 text-left font-medium">
                  <span>{label}</span>
                  <Badge
                    variant="outline"
                    className={
                      progress.blocked
                        ? "border-destructive/40 bg-destructive/10 text-destructive tabular-nums"
                        : "tabular-nums text-muted-foreground"
                    }
                  >
                    {progress.done}/{progress.total}
                  </Badge>
                </CollapsibleTrigger>
                <CollapsibleContent className="border-t p-4">
                  <ChecklistV3
                    mode={isOwnerPortalOnly ? "owner" : "admin"}
                    bedrooms={bedrooms}
                    roomTitles={apt.rooms.map((r) => r.title)}
                    keynestAvailable={state.property.keynestAvailable}
                    value={apt.checklist}
                    onChange={(next) => setChecklist(i, next)}
                  />
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      )}
    </Card>
  );
}
